import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from "../../components/AccessRestricted";
import api from "../../utils/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { SearchableSelect } from "../../components/ui/searchable-select";
import { Checkbox } from "../../components/ui/checkbox";
import { Switch } from "../../components/ui/switch";
import {
  Save,
  CheckCircle2,
  X,
  Plus,
  Loader2,
  FileText,
  ShieldCheck,
  GraduationCap,
  Users,
  Award,
  ChevronLeft,
  Monitor,
  Smartphone,
  Shield,
  Wrench,
  Package,
  RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import {
  RequirementCategory,
  mapRequirementCategoryFromApi,
} from "../../types/onboarding-requirements";

interface TemplateSection {
  id: string;
  label: string;
  count?: number;
  icon: React.ReactNode;
}

interface OnboardingTemplate {
  id: number;
  name: string;
  description: string;
  status: "Published" | "Draft";
  department?: string;
  departmentId?: number | null;
  formsCount: number;
  policiesCount: number;
  learningCount: number;
  activitiesCount: number;
  assignedTo: string[];
  updatedAt: string;
  // Full configuration data
  forms?: Array<{ id: string; name: string; description: string; required: boolean }>;
  policies?: Array<{ id: number; title: string; description: string }>;
  // Enabled course ids; null = default (the courses flagged showInOnboarding)
  learning?: number[] | null;
  supportNetwork?: Array<{ id: string; label: string; userId: number | null }>;
  licences?: Array<{ id: string; name: string; issuingAuthority: string; type: string; required: boolean }>;
  // Requirement item ids keyed by requirement category id; null = default
  // (the template tracks the live Onboarding Settings > Requirements list)
  requirements?: Record<string, string[]> | null;
}

const CategoryIcon: React.FC<{ icon: RequirementCategory["icon"]; className?: string }> = ({ icon, className }) => {
  switch (icon) {
    case "monitor": return <Monitor className={className} />;
    case "smartphone": return <Smartphone className={className} />;
    case "shield": return <Shield className={className} />;
    case "wrench": return <Wrench className={className} />;
    default: return <Package className={className} />;
  }
};

const SECTIONS: TemplateSection[] = [
  { id: "details", label: "Template Details", icon: <FileText className="w-4 h-4" /> },
  { id: "forms", label: "Forms", count: 5, icon: <FileText className="w-4 h-4" /> },
  { id: "policies", label: "Policies", count: 8, icon: <ShieldCheck className="w-4 h-4" /> },
  { id: "learning", label: "Learning", icon: <GraduationCap className="w-4 h-4" /> },
  { id: "support", label: "Support Network", icon: <Users className="w-4 h-4" /> },
  { id: "licences", label: "Licences & Qualifications", icon: <Award className="w-4 h-4" /> },
];

interface OnboardingForm {
  id: string;
  name: string;
  description: string;
  required: boolean;
  selected: boolean;
}

interface Policy {
  id: number;
  title: string;
  description: string;
  selected: boolean;
}

interface LearningCourse {
  id: number;
  title: string;
  description: string | null;
  thumbnailUrl: string | null;
  contentType: "SCORM" | "PDF" | "PPTX";
  estimatedDurationMinutes: number | null;
  issuesCertificate: boolean;
  showInOnboarding: boolean;
  scormStatus: "pending" | "processing" | "ready" | "failed" | null;
}

interface SupportContact {
  id: string;
  label: string;
  description: string;
  userId: number | null;
  icon: React.ReactNode;
}

const SUPPORT_CONTACTS: SupportContact[] = [
  { id: "hiring-manager", label: "Hiring Manager", description: "Day-to-day oversight and performance management", userId: null, icon: <Users className="w-5 h-5 text-blue-500" /> },
  { id: "onboarding-buddy", label: "Onboarding Buddy", description: "Friendly guide for the first few weeks", userId: null, icon: <Users className="w-5 h-5 text-teal-500" /> },
  { id: "hr-manager", label: "HR Manager", description: "HR queries, policies and payroll", userId: null, icon: <Users className="w-5 h-5 text-purple-500" /> },
  { id: "whs-manager", label: "WHS Manager", description: "Safety inductions and incident reporting", userId: null, icon: <ShieldCheck className="w-5 h-5 text-orange-500" /> },
  { id: "it-support", label: "IT Support", description: "System access and equipment support", userId: null, icon: <Users className="w-5 h-5 text-cyan-500" /> },
];

interface TemplateQualification {
  id: string;
  name: string;
  issuingAuthority: string;
  type: string;
  required: boolean;
}

const OnboardingTemplateEdit: React.FC = () => {
  const ability = useAbility(AbilityContext);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isNew = id === "new";

  const [activeTab, setActiveTab] = useState("details");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);

  // Form fields
  const [templateName, setTemplateName] = useState("");
  const [department, setDepartment] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [departmentOptions, setDepartmentOptions] = useState<Array<{ id: number; name: string; code: string }>>([]);
  const [description, setDescription] = useState("");
  const [assignedRoles, setAssignedRoles] = useState<string[]>([]);
  const [newRole, setNewRole] = useState("");
  const [status, setStatus] = useState<"Published" | "Draft">("Draft");

  // Forms state
  const [forms, setForms] = useState<OnboardingForm[]>([]);
  const [formsLoading, setFormsLoading] = useState(false);

  // Policies state
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [policiesLoading, setPoliciesLoading] = useState(false);

  // Support network state
  const [supportContacts, setSupportContacts] = useState<SupportContact[]>(SUPPORT_CONTACTS);
  const [users, setUsers] = useState<Array<{ id: number; name: string; jobRole: string }>>([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // Qualifications state
  const [qualifications, setQualifications] = useState<TemplateQualification[]>([]);
  const [qualificationsLoading, setQualificationsLoading] = useState(false);
  const [showAddQualification, setShowAddQualification] = useState(false);
  const [availableQualifications, setAvailableQualifications] = useState<Array<{ id: string; name: string; issuingAuthority: string; type: string }>>([]);

  // Requirements state (pre-selection for the hire form's Setup & Access step).
  // isDefaultRequirements=true means the template tracks the live Onboarding
  // Settings > Requirements list (stored as NULL); the working map is only
  // used once the Admin customizes the selection.
  const [requirementCategories, setRequirementCategories] = useState<RequirementCategory[]>([]);
  const [requirementsLoading, setRequirementsLoading] = useState(false);
  const [isDefaultRequirements, setIsDefaultRequirements] = useState(true);
  const [selectedRequirements, setSelectedRequirements] = useState<Record<string, Set<string>>>({});

  // Learning state (Idarah Learning courses assigned by this template).
  // isDefaultLearning=true means the template tracks the courses flagged
  // showInOnboarding (stored as NULL); the working set is only used once the
  // Admin customizes the selection.
  const [learningCourses, setLearningCourses] = useState<LearningCourse[]>([]);
  // True from the start: the catalogue is fetched on mount (the tab chip shows its size).
  const [learningLoading, setLearningLoading] = useState(true);
  const [isDefaultLearning, setIsDefaultLearning] = useState(true);
  const [selectedCourses, setSelectedCourses] = useState<Set<number>>(new Set());

  const canEdit = isNew
    ? ability.can(ACTIONS.CREATE, RESOURCES.ONBOARDING_DASHBOARD)
    : ability.can(ACTIONS.UPDATE, RESOURCES.ONBOARDING_DASHBOARD);

  useEffect(() => {
    if (!canEdit) {
      setLoading(false);
      return;
    }

    const fetchTemplate = async () => {
      if (isNew) {
        setLoading(false);
        return;
      }

      try {
        const res = await api.get<{ success: boolean; data: OnboardingTemplate }>(
          `/api/onboarding/templates/${id}`,
          { skipErrorToast: true }
        );

        if (res.success && res.data) {
          const t = res.data;
          setTemplateName(t.name);
          setDepartment(t.department || "");
          setDepartmentId(t.departmentId ? String(t.departmentId) : "");
          setDescription(t.description || "");
          setAssignedRoles(t.assignedTo || []);
          setStatus(t.status);
          // Load saved tab data so it round-trips on the next save
          if (Array.isArray(t.forms)) {
            setForms(
              t.forms.map((f: any) => ({
                id: f.id,
                name: f.name,
                description: f.description || "",
                required: f.required ?? false,
                selected: true,
              }))
            );
          }
          if (Array.isArray(t.policies)) {
            setPolicies(
              t.policies.map((p: any) => ({
                id: p.id,
                title: p.title,
                description: p.description || "",
                selected: true,
              }))
            );
          }
          if (t.supportNetwork) {
            setSupportContacts((prev) =>
              prev.map((c) => {
                const match = t.supportNetwork?.find((sn: any) => sn.id === c.id);
                return match ? { ...c, userId: match.userId } : c;
              })
            );
          }
          if (Array.isArray(t.licences)) {
            setQualifications(
              t.licences.map((l: any) => ({
                id: l.id,
                name: l.name,
                issuingAuthority: l.issuingAuthority,
                type: l.type,
                required: l.required ?? true,
              }))
            );
          }
          // Requirements: null means the template follows the live settings
          // list; a stored map is an Admin-curated selection.
          if (t.requirements && typeof t.requirements === "object") {
            const restored: Record<string, Set<string>> = {};
            Object.entries(t.requirements).forEach(([categoryId, itemIds]) => {
              if (Array.isArray(itemIds)) restored[categoryId] = new Set(itemIds.map(String));
            });
            setSelectedRequirements(restored);
            setIsDefaultRequirements(false);
          } else {
            setSelectedRequirements({});
            setIsDefaultRequirements(true);
          }
          // Learning: null means the template follows the courses flagged for
          // onboarding; a stored array is an Admin-curated selection.
          if (Array.isArray(t.learning)) {
            setSelectedCourses(new Set(t.learning.map((courseId) => Number(courseId))));
            setIsDefaultLearning(false);
          } else {
            setSelectedCourses(new Set());
            setIsDefaultLearning(true);
          }
        } else {
          toast.error("Template not found");
          navigate("/onboarding/templates");
        }
      } catch (error) {
        console.error("Error fetching template:", error);
        toast.error("Failed to load template");
        navigate("/onboarding/templates");
      } finally {
        setLoading(false);
      }
    };

    fetchTemplate();
  }, [id, isNew, canEdit]);

  // Fetch real departments for the Template Details dropdown
  useEffect(() => {
    if (!canEdit) return;

    const fetchDepartments = async () => {
      try {
        const res = await api.get<{
          success: boolean;
          departments: Array<{ id: number; departmentName: string; departmentCode: string }>;
        }>("/api/departments/selection", { skipErrorToast: true });

        if (res.success && res.departments) {
          setDepartmentOptions(
            res.departments.map((d) => ({
              id: d.id,
              name: d.departmentName,
              code: d.departmentCode,
            }))
          );
        }
      } catch (error) {
        console.error("Error fetching departments:", error);
        toast.error("Failed to load departments");
      }
    };

    fetchDepartments();
  }, [canEdit]);

  // Preselect the department for legacy templates that only stored the name
  useEffect(() => {
    if (departmentId || !department || departmentOptions.length === 0) return;
    const match = departmentOptions.find((d) => d.name === department);
    if (match) {
      setDepartmentId(match.id.toString());
    }
  }, [departmentOptions, department, departmentId]);

  // Load the requirement categories configured in Onboarding Settings so the
  // Requirements section can mirror them. Unlike the hire form there is no
  // bundled fallback here — the editor must show the live settings list.
  useEffect(() => {
    if (!canEdit) return;

    const fetchRequirementCategories = async () => {
      setRequirementsLoading(true);
      try {
        const res = await api.get<{ success: boolean; data: any[] }>(
          "/api/onboarding/requirements/categories",
          { skipErrorToast: true }
        );
        if (res.success && Array.isArray(res.data)) {
          setRequirementCategories(res.data.map(mapRequirementCategoryFromApi));
        }
      } catch (error) {
        console.error("Error fetching requirement categories:", error);
        toast.error("Failed to load onboarding requirements");
      } finally {
        setRequirementsLoading(false);
      }
    };

    fetchRequirementCategories();
  }, [canEdit]);

  // Fetch available onboarding forms when the forms tab is active
  useEffect(() => {
    if (activeTab !== "forms") return;

    const fetchForms = async () => {
      setFormsLoading(true);
      try {
        const res = await api.get<{
          success: boolean;
          data: Array<{ id: string; name: string; description: string; required: boolean }>;
        }>("/api/onboarding/forms", { skipErrorToast: true });

        if (res.success && res.data) {
          // Merge the registry with the current selection state
          setForms((prev) => {
            const selectedIds = new Set(prev.filter((f) => f.selected).map((f) => f.id));
            return res.data.map((f) => ({
              id: f.id,
              name: f.name,
              description: f.description || "",
              required: f.required ?? false,
              selected: selectedIds.has(f.id),
            }));
          });
        }
      } catch (error) {
        console.error("Error fetching forms:", error);
        toast.error("Failed to load forms");
      } finally {
        setFormsLoading(false);
      }
    };

    fetchForms();
  }, [activeTab]);

  // Fetch active policies when policies tab is active
  useEffect(() => {
    if (activeTab !== "policies") return;

    const fetchPolicies = async () => {
      setPoliciesLoading(true);
      try {
        const res = await api.get<{ success: boolean; data: Array<{ id: number; title: string; description: string }> }>(
          "/api/onboarding/policies",
          { skipErrorToast: true }
        );

        if (res.success && res.data && res.data.length > 0) {
          // Merge fetched policies with current selection state
          setPolicies((prev) => {
            const selectedIds = new Set(prev.filter((p) => p.selected).map((p) => p.id));
            return res.data.map((p) => ({
              id: p.id,
              title: p.title,
              description: p.description || "",
              selected: selectedIds.has(p.id),
            }));
          });
        }
      } catch (error) {
        console.error("Error fetching policies:", error);
      } finally {
        setPoliciesLoading(false);
      }
    };

    fetchPolicies();
  }, [activeTab]);

  // Fetch users when support network tab is active
  useEffect(() => {
    if (activeTab !== "support") return;

    const fetchUsers = async () => {
      setUsersLoading(true);
      try {
        const res = await api.get<{ success: boolean; users: Array<{ id: number; name: string; jobRole: string }> }>(
          "/api/users?all_users=true",
          { skipErrorToast: true }
        );

        if (res.success && res.users) {
          setUsers(res.users);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        toast.error("Failed to load users");
      } finally {
        setUsersLoading(false);
      }
    };

    fetchUsers();
  }, [activeTab]);

  // Fetch qualification presets when licences tab is active
  useEffect(() => {
    if (activeTab !== "licences") return;

    const fetchQualifications = async () => {
      setQualificationsLoading(true);
      try {
        const res = await api.get<{ success: boolean; data: Array<{ id: string; name: string; issuingAuthority: string; type: { name: string } | null }> }>(
          "/api/onboarding/qualifications",
          { skipErrorToast: true }
        );

        if (res.success && res.data && res.data.length > 0) {
          setAvailableQualifications(
            res.data.map((q) => ({
              id: q.id,
              name: q.name,
              issuingAuthority: q.issuingAuthority || "",
              type: q.type?.name || "Qualification",
            }))
          );
        }
      } catch (error) {
        console.error("Error fetching qualifications:", error);
      } finally {
        setQualificationsLoading(false);
      }
    };

    fetchQualifications();
  }, [activeTab]);

  // Fetch the Idarah Learning catalogue on load, not on tab activation: the Learning tab's
  // chip in the tab bar shows how many courses this template requires, so the count has to be
  // right before the tab is ever opened. (The Template Details tab loads the requirement
  // categories on mount for the same reason.)
  useEffect(() => {
    if (!canEdit) return;

    const fetchCourses = async () => {
      setLearningLoading(true);
      try {
        const res = await api.get<{ success: boolean; data: LearningCourse[] }>(
          "/api/onboarding/courses",
          { skipErrorToast: true }
        );

        if (res.success && Array.isArray(res.data)) {
          setLearningCourses(res.data);
        }
      } catch (error) {
        console.error("Error fetching courses:", error);
        toast.error("Failed to load courses");
      } finally {
        setLearningLoading(false);
      }
    };

    fetchCourses();
  }, [canEdit]);

  const addRole = () => {
    if (newRole.trim() && !assignedRoles.includes(newRole.trim())) {
      setAssignedRoles((prev) => [...prev, newRole.trim()]);
      setNewRole("");
    }
  };

  const removeRole = (role: string) => {
    setAssignedRoles((prev) => prev.filter((r) => r !== role));
  };

  // Full live selection — used when the Admin customizes a template that was
  // still on the default (all settings items) selection.
  const buildFullRequirementsSelection = (): Record<string, Set<string>> =>
    Object.fromEntries(
      requirementCategories.map((c) => [c.id, new Set(c.items.map((i) => i.id))])
    );

  const toggleTemplateRequirement = (categoryId: string, itemId: string) => {
    setSelectedRequirements((prev) => {
      const base = isDefaultRequirements ? buildFullRequirementsSelection() : prev;
      const items = new Set(base[categoryId] ?? []);
      if (items.has(itemId)) {
        items.delete(itemId);
      } else {
        items.add(itemId);
      }
      return { ...base, [categoryId]: items };
    });
    setIsDefaultRequirements(false);
  };

  // A course is enabled when the template is on the default (courses flagged
  // for onboarding) or the Admin has explicitly selected it.
  const isCourseEnabled = (course: LearningCourse): boolean =>
    isDefaultLearning ? course.showInOnboarding : selectedCourses.has(course.id);

  // Full default selection — used when the Admin customizes a template that
  // was still on the default (flagged courses) selection.
  const buildFullCourseSelection = (): Set<number> =>
    new Set(learningCourses.filter((c) => c.showInOnboarding).map((c) => c.id));

  const toggleTemplateCourse = (courseId: number) => {
    setSelectedCourses((prev) => {
      const base = isDefaultLearning ? buildFullCourseSelection() : prev;
      const next = new Set(base);
      if (next.has(courseId)) {
        next.delete(courseId);
      } else {
        next.add(courseId);
      }
      return next;
    });
    setIsDefaultLearning(false);
  };

  // null = default (track live settings list); otherwise a map of category
  // id -> selected item ids, with ids that no longer exist in settings
  // filtered out.
  const buildTemplateRequirementsPayload = (): Record<string, string[]> | null => {
    if (isDefaultRequirements) return null;
    if (requirementCategories.length === 0) {
      // Categories failed to load — persist the raw selection untouched
      return Object.fromEntries(
        Object.entries(selectedRequirements).map(([categoryId, ids]) => [categoryId, Array.from(ids)])
      );
    }
    const payload: Record<string, string[]> = {};
    requirementCategories.forEach((category) => {
      const selected = selectedRequirements[category.id];
      if (!selected) return;
      const ids = category.items.map((i) => i.id).filter((itemId) => selected.has(itemId));
      if (ids.length > 0) payload[category.id] = ids;
    });
    return payload;
  };

  const handleSave = async (publish = false) => {
    if (!templateName.trim()) {
      toast.error("Template name is required");
      return;
    }

    if (publish) {
      setPublishing(true);
    } else {
      setSaving(true);
    }

    try {
      const payload = {
        name: templateName,
        description,
        departmentId: departmentId ? parseInt(departmentId) : null,
        assignedTo: assignedRoles,
        status: publish ? "Published" : "Draft",
        forms: forms.filter((f) => f.selected).map((f) => ({ id: f.id, name: f.name, description: f.description, required: f.required })),
        policies: policies.filter((p) => p.selected).map((p) => ({ id: p.id, title: p.title, description: p.description })),
        supportNetwork: supportContacts
          .filter((c) => c.userId !== null)
          .map((c) => ({ id: c.id, label: c.label, userId: c.userId })),
        licences: qualifications.map((q) => ({
          id: q.id,
          name: q.name,
          issuingAuthority: q.issuingAuthority,
          type: q.type,
          required: q.required,
        })),
        requirements: buildTemplateRequirementsPayload(),
        learning: isDefaultLearning ? null : Array.from(selectedCourses),
      };

      let res;
      if (isNew) {
        res = await api.post<{ success: boolean; data: OnboardingTemplate; message: string }>(
          "/api/onboarding/templates",
          payload
        );
      } else {
        res = await api.put<{ success: boolean; data: OnboardingTemplate; message: string }>(
          `/api/onboarding/templates/${id}`,
          payload
        );
      }

      if (res.success) {
        toast.success(publish ? "Template published successfully" : "Draft saved");
        if (publish) {
          navigate("/onboarding/templates");
        } else if (isNew && res.data?.id) {
          // Stay on the editor and switch from create to edit mode, so
          // subsequent saves update this template instead of duplicating it.
          navigate(`/onboarding/templates/${res.data.id}`, { replace: true });
        }
      } else {
        toast.error(res.message || "Failed to save template");
      }
    } catch (error: any) {
      console.error("Error saving template:", error);
      toast.error(error.message || "Failed to save template");
    } finally {
      setSaving(false);
      setPublishing(false);
    }
  };

  const configuredSections = 1; // Template Details is always configured if name is set
  const totalSections = SECTIONS.length;

  // The Learning chip shows how many courses this template actually requires — the enabled
  // ones, which for a default template are the courses flagged for onboarding. It stays hidden
  // until the catalogue has loaded so it never flashes a misleading 0.
  const enabledLearningCount = learningCourses.filter(isCourseEnabled).length;
  const tabSections: TemplateSection[] = SECTIONS.map((section) =>
    section.id === "learning"
      ? { ...section, count: learningLoading ? undefined : enabledLearningCount }
      : section
  );

  if (!canEdit) {
    return <AccessRestricted />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-900" />
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-6 animate-in fade-in duration-500 pb-10 px-0 sm:px-4 lg:px-0">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <button onClick={() => navigate("/onboarding")} className="hover:text-gray-900 transition-colors">
          Onboarding
        </button>
        <span>/</span>
        <button onClick={() => navigate("/onboarding/templates")} className="hover:text-gray-900 transition-colors">
          Templates
        </button>
        <span>/</span>
        <span className="text-gray-900 font-medium">{isNew ? "New Template" : templateName}</span>
      </div>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
            {isNew ? "New Onboarding Template" : `Edit: ${templateName}`}
          </h1>
          <p className="text-gray-500 text-sm font-medium">
            Configure exactly what the new hire will see in their onboarding portal
          </p>
        </div>
        <div className="flex items-center gap-3">
          {!isNew && (
            <Badge
              variant="outline"
              className={
                status === "Published"
                  ? "bg-green-50 text-green-700 border-green-200"
                  : "bg-amber-50 text-amber-700 border-amber-200"
              }
            >
              {status}
            </Badge>
          )}
          <Button
            variant="outline"
            className="rounded-xl gap-2"
            onClick={() => handleSave(false)}
            disabled={saving || publishing}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Draft
          </Button>
          <Button
            className="bg-green-600 hover:bg-green-700 text-white rounded-xl gap-2"
            onClick={() => handleSave(true)}
            disabled={saving || publishing}
          >
            {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Publish
          </Button>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Template completion</span>
          <span className="text-sm text-gray-500">{configuredSections}/{totalSections} sections configured</span>
        </div>
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-slate-900 rounded-full transition-all"
            style={{ width: `${(configuredSections / totalSections) * 100}%` }}
          />
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl overflow-x-auto">
        {tabSections.map((section) => (
          <button
            key={section.id}
            onClick={() => setActiveTab(section.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === section.id
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {section.icon}
            <span>{section.label}</span>
            {section.count !== undefined && (
              <span className={`text-xs ${activeTab === section.id ? "text-gray-500" : "text-gray-400"}`}>
                {section.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        {activeTab === "details" && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Template Details</h2>
              <p className="text-sm text-gray-500">Name and configure who this template applies to</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-900">
                  Template Name <span className="text-red-500">*</span>
                </label>
                <Input
                  placeholder="e.g. Scaffolder Onboarding"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  className="h-11 rounded-xl border-gray-200 bg-gray-50"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-900">Department</label>
                <SearchableSelect
                  groups={[
                    {
                      heading: "Departments",
                      options: departmentOptions.map((d) => ({
                        value: d.id.toString(),
                        label: d.name,
                        subtitle: d.code || undefined,
                      })),
                    },
                  ]}
                  value={departmentId}
                  onChange={(value) => setDepartmentId(value === "none" ? "" : value)}
                  placeholder="Select department"
                  searchPlaceholder="Search departments..."
                  emptyMessage="No departments found."
                  noneOption={{ value: "none", label: "No department" }}
                  className="h-11 rounded-xl border-gray-200 bg-gray-50"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-900">Description</label>
              <Textarea
                placeholder="Describe what this template covers..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="min-h-[80px] rounded-xl border-gray-200 bg-gray-50 resize-y"
              />
            </div>

            <div className="space-y-3 pt-4 border-t border-gray-100">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Assign to Job Roles / Employment Types</h3>
                <p className="text-sm text-gray-500">
                  This template will be suggested when onboarding someone in one of these roles
                </p>
              </div>

              <div className="flex gap-2">
                <Input
                  placeholder="e.g. Scaffolder"
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addRole()}
                  className="h-10 rounded-xl border-gray-200 bg-gray-50"
                />
                <Button variant="outline" size="sm" onClick={addRole} className="rounded-xl px-3">
                  <Plus size={16} />
                </Button>
              </div>

              <div className="flex flex-wrap gap-2">
                {assignedRoles.map((role) => (
                  <span
                    key={role}
                    className="inline-flex items-center gap-1 text-sm bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg font-medium"
                  >
                    {role}
                    <button
                      onClick={() => removeRole(role)}
                      className="ml-1 text-gray-400 hover:text-gray-600"
                    >
                      <X size={14} />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t border-gray-100">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Requirements</h3>
                  <p className="text-sm text-gray-500">
                    Pre-select the Setup &amp; Access items from Onboarding Settings &gt; Requirements for new hires using this template
                  </p>
                </div>
                {isDefaultRequirements ? (
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 shrink-0 self-start">
                    Default — all settings items
                  </Badge>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-xl gap-1.5 shrink-0 self-start"
                    onClick={() => setIsDefaultRequirements(true)}
                  >
                    <RotateCcw size={14} />
                    Reset to default
                  </Button>
                )}
              </div>

              {isDefaultRequirements && (
                <p className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                  This template includes every item from Onboarding Settings &gt; Requirements and stays in sync as
                  items are added or removed there. Unselect any item below to customize the list for this template.
                </p>
              )}

              {requirementsLoading ? (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading requirements...
                </div>
              ) : requirementCategories.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No requirement categories configured yet. Add them under Onboarding Settings &gt; Requirements.
                </p>
              ) : (
                requirementCategories.map((category) => {
                  const isItemSelected = (itemId: string) =>
                    isDefaultRequirements ? true : selectedRequirements[category.id]?.has(itemId) || false;
                  const selectedCount = isDefaultRequirements
                    ? category.items.length
                    : category.items.filter((i) => selectedRequirements[category.id]?.has(i.id)).length;
                  return (
                    <div key={category.id} className="border border-gray-100 rounded-xl p-4 space-y-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg ${category.iconBg} flex items-center justify-center`}>
                          <CategoryIcon icon={category.icon} className={`w-4 h-4 ${category.iconColor}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-semibold text-gray-900">{category.name}</h4>
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                              {selectedCount}/{category.items.length} selected
                            </span>
                          </div>
                          <p className="text-xs text-gray-500">{category.description}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {category.items.map((item) => (
                          <label
                            key={item.id}
                            className={`flex items-center gap-2.5 p-3 rounded-lg border cursor-pointer transition-all ${
                              isItemSelected(item.id)
                                ? "border-blue-500 bg-blue-50"
                                : "border-gray-200 hover:border-gray-300"
                            }`}
                          >
                            <Checkbox
                              checked={isItemSelected(item.id)}
                              onCheckedChange={() => toggleTemplateRequirement(category.id, item.id)}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
                            </div>
                            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded shrink-0">
                              {item.lead}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {activeTab === "forms" && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-500" />
                Forms
              </h2>
              <p className="text-sm text-gray-500">Select which forms the new hire must complete before their start date</p>
            </div>

            {formsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : forms.length === 0 ? (
              <div className="bg-gray-50 border border-gray-100 rounded-2xl p-12 text-center">
                <FileText className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                <p className="text-sm font-medium text-gray-500">No forms available</p>
                <p className="text-xs text-gray-400 mt-1">There are no onboarding forms available to assign yet</p>
              </div>
            ) : (
            <div className="space-y-3">
              {forms.map((form) => (
                <label
                  key={form.id}
                  className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-colors ${
                    form.selected
                      ? "bg-blue-50/50 border-blue-200"
                      : "bg-white border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <div className="flex-shrink-0">
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                        form.selected
                          ? "bg-blue-500 border-blue-500"
                          : "border-gray-300 bg-white"
                      }`}
                    >
                      {form.selected && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900">{form.name}</p>
                    <p className="text-sm text-gray-500">{form.description}</p>
                  </div>
                  {form.required && (
                    <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200 text-xs font-medium flex-shrink-0">
                      Required
                    </Badge>
                  )}
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={form.selected}
                    onChange={() => {
                      setForms((prev) =>
                        prev.map((f) =>
                          f.id === form.id ? { ...f, selected: !f.selected } : f
                        )
                      );
                    }}
                  />
                </label>
              ))}
            </div>
            )}
          </div>
        )}

        {activeTab === "policies" && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-green-500" />
                Policies
              </h2>
              <p className="text-sm text-gray-500">
                Choose the policies your new hire must read and acknowledge before their start date. Policies are sourced directly from your Policies module and stay in sync automatically.
              </p>
            </div>

            {policiesLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : (
              <div className="space-y-3">
                {policies.map((policy) => (
                  <label
                    key={policy.id}
                    className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-colors ${
                      policy.selected
                        ? "bg-green-50/50 border-green-200"
                        : "bg-white border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex-shrink-0">
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                          policy.selected
                            ? "bg-green-500 border-green-500"
                            : "border-gray-300 bg-white"
                        }`}
                      >
                        {policy.selected && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">{policy.title}</p>
                      {policy.description && (
                        <p className="text-sm text-gray-500">{policy.description}</p>
                      )}
                    </div>
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={policy.selected}
                      onChange={() => {
                        setPolicies((prev) =>
                          prev.map((p) =>
                            p.id === policy.id ? { ...p, selected: !p.selected } : p
                          )
                        );
                      }}
                    />
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "learning" && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <GraduationCap className="w-5 h-5 text-indigo-500" />
                    Learning
                  </h2>
                  {!learningLoading && learningCourses.length > 0 && (
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                      {learningCourses.filter(isCourseEnabled).length} of {learningCourses.length} enabled
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500">
                  Assign the Idarah Learning courses the new hire should complete. Enabled courses
                  appear in the hire&apos;s onboarding portal and are tracked there; they do not block
                  conversion — add each completed course to the learner profile afterwards.
                </p>
              </div>
              {isDefaultLearning ? (
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 shrink-0 self-start">
                  Default — courses flagged for onboarding
                </Badge>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-xl gap-1.5 shrink-0 self-start"
                  onClick={() => {
                    setSelectedCourses(new Set());
                    setIsDefaultLearning(true);
                  }}
                >
                  <RotateCcw size={14} />
                  Reset to default
                </Button>
              )}
            </div>

            {isDefaultLearning && (
              <p className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                This template includes every course flagged for onboarding in the Learning module and stays in sync as
                courses change there. Toggle any course below to customize the list for this template.
              </p>
            )}

            {learningLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : learningCourses.length === 0 ? (
              <div className="bg-gray-50 border border-gray-100 rounded-2xl p-12 text-center">
                <GraduationCap className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                <p className="text-sm font-medium text-gray-500">No courses available</p>
                <p className="text-xs text-gray-400 mt-1">Courses are created in the Learning module</p>
              </div>
            ) : (
              <div className="space-y-3">
                {learningCourses.map((course) => {
                  const enabled = isCourseEnabled(course);
                  return (
                    <div
                      key={course.id}
                      className={`flex items-center gap-4 p-4 rounded-xl border transition-colors ${
                        enabled
                          ? "bg-indigo-50/50 border-indigo-200"
                          : "bg-white border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden bg-indigo-100 flex items-center justify-center">
                        {course.thumbnailUrl ? (
                          <img src={course.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <GraduationCap className="w-5 h-5 text-indigo-500" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900">{course.title}</p>
                        {course.description && (
                          <p className="text-sm text-gray-500 line-clamp-2">{course.description}</p>
                        )}
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                            {course.contentType}
                            {course.estimatedDurationMinutes != null
                              ? ` · ${course.estimatedDurationMinutes} min`
                              : ""}
                          </span>
                          {course.issuesCertificate && (
                            <Badge
                              variant="outline"
                              className="bg-amber-50 text-amber-700 border-amber-200 text-xs font-medium"
                            >
                              Certificate
                            </Badge>
                          )}
                        </div>
                      </div>
                      <Switch
                        checked={enabled}
                        onCheckedChange={() => toggleTemplateCourse(course.id)}
                        aria-label={`${enabled ? "Disable" : "Enable"} ${course.title}`}
                        className="shrink-0"
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === "support" && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Users className="w-5 h-5 text-teal-500" />
                Support Network
              </h2>
              <p className="text-sm text-gray-500">
                Assign the people the new hire will see as their support contacts in the portal
              </p>
            </div>

            {usersLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : (
              <div className="space-y-4">
                {supportContacts.map((contact) => (
                  <div
                    key={contact.id}
                    className="flex items-center gap-4 p-4 rounded-xl border border-gray-100 bg-white"
                  >
                    <div className="flex-shrink-0 p-2 bg-gray-50 rounded-lg">
                      {contact.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">{contact.label}</p>
                      <p className="text-sm text-gray-500">{contact.description}</p>
                    </div>
                    <div className="w-56">
                      <Select
                        value={contact.userId?.toString() || "none"}
                        onValueChange={(value) => {
                          setSupportContacts((prev) =>
                            prev.map((c) =>
                              c.id === contact.id ? { ...c, userId: value === "none" ? null : parseInt(value) } : c
                            )
                          );
                        }}
                      >
                        <SelectTrigger className="h-10 rounded-xl border-gray-200 bg-gray-50">
                          <SelectValue placeholder="Select a user">
                            {contact.userId
                              ? users.find((u) => u.id === contact.userId)?.name || "Select a user"
                              : "Select a user"}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent className="rounded-xl max-h-60">
                          <div className="px-3 py-2 bg-white border-b border-gray-100 sticky top-0 z-10">
                            <Input
                              placeholder="Search users..."
                              className="h-8 text-sm rounded-lg"
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                const query = e.target.value.toLowerCase();
                                const items = document.querySelectorAll(`[data-contact-id="${contact.id}"]`);
                                items.forEach((item) => {
                                  const text = item.textContent?.toLowerCase() || "";
                                  (item as HTMLElement).style.display = text.includes(query) ? "" : "none";
                                });
                              }}
                            />
                          </div>
                          <div className="py-1">
                            <SelectItem value="none" className="rounded-lg">
                              <span className="text-gray-400">None selected</span>
                            </SelectItem>
                            {users.map((user) => (
                              <SelectItem
                                key={user.id}
                                value={user.id.toString()}
                                className="rounded-lg"
                                data-contact-id={contact.id}
                              >
                                <div className="flex flex-col">
                                  <span className="font-medium">{user.name}</span>
                                  <span className="text-xs text-gray-500">{user.jobRole}</span>
                                </div>
                              </SelectItem>
                            ))}
                          </div>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "licences" && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-500" />
                Licences & Qualifications
              </h2>
              <p className="text-sm text-gray-500">
                Define which licences and qualifications are required or optional for this role's onboarding template.
              </p>
            </div>

            {/* Add Qualification Button */}
            <Button
              variant="outline"
              className="w-full rounded-xl border-dashed border-gray-300 hover:border-gray-400 hover:bg-gray-50 gap-2 py-6"
              onClick={() => setShowAddQualification(true)}
            >
              <Plus className="w-4 h-4" />
              Add Qualification
            </Button>

            {/* Qualifications List */}
            {qualifications.length === 0 ? (
              <div className="bg-gray-50 border border-gray-100 rounded-2xl p-12 text-center">
                <Award className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                <p className="text-sm font-medium text-gray-500">No qualifications added yet</p>
                <p className="text-xs text-gray-400 mt-1">Use the button above to add required licences and qualifications</p>
              </div>
            ) : (
              <div className="space-y-3">
                {qualifications.map((qual) => (
                  <div
                    key={qual.id}
                    className="flex items-center gap-4 p-4 rounded-xl border border-gray-100 bg-white hover:shadow-sm transition-shadow"
                  >
                    <div className="flex-shrink-0 p-2 bg-amber-50 rounded-lg">
                      <Award className="w-5 h-5 text-amber-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-gray-900">{qual.name}</p>
                        <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-200 text-xs">
                          {qual.type}
                        </Badge>
                      </div>
                      {qual.issuingAuthority && (
                        <p className="text-xs text-gray-500">{qual.issuingAuthority}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={qual.required}
                          onChange={() => {
                            setQualifications((prev) =>
                              prev.map((q) =>
                                q.id === qual.id ? { ...q, required: !q.required } : q
                              )
                            );
                          }}
                          className="w-4 h-4 rounded border-gray-300 text-amber-500 focus:ring-amber-500"
                        />
                        <span className="text-sm text-gray-600">Required</span>
                      </label>
                      <button
                        onClick={() => {
                          setQualifications((prev) => prev.filter((q) => q.id !== qual.id));
                        }}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add Qualification Dialog */}
            {showAddQualification && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white rounded-2xl p-6 w-full max-w-lg mx-4 shadow-xl">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Add Qualification</h3>
                    <button
                      onClick={() => setShowAddQualification(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X size={20} />
                    </button>
                  </div>
                  <p className="text-sm text-gray-500 mb-4">Select a qualification to add to this template</p>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {availableQualifications.length > 0 ? (
                      availableQualifications.map((qual) => (
                        <button
                          key={qual.id}
                          className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors text-left"
                          onClick={() => {
                            if (!qualifications.find((q) => q.id === qual.id)) {
                              setQualifications((prev) => [
                                ...prev,
                                {
                                  id: qual.id,
                                  name: qual.name,
                                  issuingAuthority: qual.issuingAuthority,
                                  type: qual.type,
                                  required: true,
                                },
                              ]);
                            }
                            setShowAddQualification(false);
                          }}
                        >
                          <Award className="w-5 h-5 text-amber-500 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">{qual.name}</p>
                            <p className="text-xs text-gray-500">{qual.issuingAuthority} · {qual.type}</p>
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-400">
                        <Award className="w-10 h-10 mx-auto mb-2" />
                        <p className="text-sm">No qualification presets available</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom Action Bar */}
      <div className="bg-white border-t border-gray-200 px-6 py-4 -mx-0 sm:-mx-4 lg:-mx-0">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            className="rounded-xl text-gray-500 hover:text-gray-700"
            onClick={() => navigate("/onboarding/templates")}
          >
            Cancel
          </Button>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              className="rounded-xl gap-2"
              onClick={() => handleSave(false)}
              disabled={saving || publishing}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Draft
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700 text-white rounded-xl gap-2"
              onClick={() => handleSave(true)}
              disabled={saving || publishing}
            >
              {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Publish Template
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTemplateEdit;
